import { IPaymentInfo, IScheduledPaymentData, MSSNotification, MSSNotificationType } from '@lib/types';
import { IMemberDetails } from '../../state/model';
import dayjs from 'dayjs';
import { getMembershipTitle, stringHelper } from '../../utils/stringHelper';
import { CurrentPayPlan, MembershipLevel } from '@ace-clublabs/common-membership-api-types';
import { navigateToMembershipBillPay } from '../../utils/navigateToMembershipBillPay';
import { getIsUpgradingBenefitLevel } from '../../state/helpers';
type IHistory = {
  push: (page: string) => void;
};

export const buildNotifications = (
  memberDetails: IMemberDetails,
  paymentInfo: IPaymentInfo,
  tags: object | undefined,
  history: IHistory
) => {
  const notifications = [
    buildBenefitEffectiveNotification(memberDetails),
    buildRenewMembershipNotification(memberDetails, paymentInfo, tags, history),
    buildRenewalPaymentScheduledNotification(memberDetails, paymentInfo),
    buildPendingCancellationNotification(memberDetails, tags, history),
  ].filter((notification) => notification) as MSSNotification[];

  return notifications;
};

const buildBenefitEffectiveNotification = (memberDetails: IMemberDetails) => {
  const isUpgradingBenefitLevel = getIsUpgrading(memberDetails);
  const { products } = memberDetails;

  if (isUpgradingBenefitLevel) {
    const pendingLevelInfo = getPendingLevel(memberDetails);
    const pendingLevel = getMembershipTitle(memberDetails, pendingLevelInfo?.pendingLevel!);
    const benefitUpgradeEffectiveDate = formatDate(pendingLevelInfo?.effectiveDate!);

    const benefitEffectiveNotification = {
      type: MSSNotificationType.WARNING,
      boldText: <>Your {pendingLevel} benefits including extended Roadside Assistance</>,
      message: `are effective ${benefitUpgradeEffectiveDate}.`,
      iconOverride: 'bullhorn-regular',
    };

    return benefitEffectiveNotification;
  }

  return null;
};

const buildRenewMembershipNotification = (
  memberDetails: IMemberDetails,
  paymentInfo: IPaymentInfo,
  tags: object | undefined,
  history: IHistory
) => {
  const inRenewalAndNotInAutoPay =
    memberDetails?.inRenewal &&
    memberDetails?.currentBillPlan === CurrentPayPlan.ONETIME &&
    typeof paymentInfo?.scheduledPaymentData !== 'object' &&
    !memberDetails.hasPaymentInTransit;

  if (inRenewalAndNotInAutoPay) {
    const renewYourMembershipNotification: MSSNotification = {
      type: MSSNotificationType.WARNING,
      boldText: `Renew your membership online to avoid cancellation.`,
      onClick: async () => await navigateToMembershipBillPay(memberDetails, tags, history),
    };

    return renewYourMembershipNotification;
  }

  return null;
};

const buildRenewalPaymentScheduledNotification = (memberDetails: IMemberDetails, paymentInfo: IPaymentInfo) => {
  const renewalPaymentScheduled = memberDetails?.inRenewal && typeof paymentInfo?.scheduledPaymentData === 'object';
  if (renewalPaymentScheduled) {
    const { paymentAmount, scheduledDate } = paymentInfo?.scheduledPaymentData as IScheduledPaymentData;
    const renewalPaymentScheduledNotification: MSSNotification = {
      type: MSSNotificationType.SUCCESS,
      boldText: `Renewal payment has been scheduled.`,
      message: `An amount of ${stringHelper.formatMoney(paymentAmount)} will be debited on ${formatDate(
        scheduledDate
      )}.`,
    };

    return renewalPaymentScheduledNotification;
  }

  return null;
};

const buildPendingCancellationNotification = (
  memberDetails: IMemberDetails,
  tags: object | undefined,
  history: IHistory
) => {
  const pendingCancellation = memberDetails?.inArrears && !memberDetails?.hasPaymentInTransit;
  if (pendingCancellation) {
    const pendingCancellationNotification: MSSNotification = {
      type: MSSNotificationType.ERROR,
      boldText: `Pay now; your membership is pending cancellation`,
      onClick: async () => await navigateToMembershipBillPay(memberDetails, tags, history),
    };

    return pendingCancellationNotification;
  }

  return null;
};

export const getPendingLevel = (memberDetails: IMemberDetails) => {
  const { productType: pendingLevel, effectiveDate } =
    memberDetails?.pendingProducts?.find((product) => product.productType in MembershipLevel) || {};

  return {
    pendingLevel: pendingLevel as MembershipLevel,
    effectiveDate,
  };
};

export const getIsUpgrading = (memberDetails: IMemberDetails) => {
  const { pendingLevel } = getPendingLevel(memberDetails);
  const { existingLevel: currentBenefitLevel } = memberDetails?.benefitLevels || {};

  return getIsUpgradingBenefitLevel(currentBenefitLevel!, pendingLevel);
};

const formatDate = (date: string | dayjs.Dayjs) => dayjs(date).format('MMMM D, YYYY');
